import { omit, capitalize, cloneDeep } from 'lodash'

import * as lineColors from '../store/lineColors'
import { newFileFamilyTrees } from '../store/newFileState'
import { emptyFamilyTree } from '../store/initialState'
import {
  FILE_LOADED,
  SAVE_FAMILY_TREE,
  SET_CURRENT_FAMILY_TREE_BACKGROUND,
  NEW_RELATIONSHIP_TYPE,
  DELETE_SELECTED_RELATIONSHIP,
  SET_RELATIONSHIP_TYPE_COLOUR,
  SET_SELECTED_RELATIONSHIP_NAME,
  SET_SELECTED_RELATIONSHIP_TYPE,
  SET_SELECTED_RELATIONSHIP_LINE_STYLE,
  NEW_FAMILY_TREE,
  DELETE_FAMILY_TREE,
  EDIT_FAMILY_TREE_NAME,
  DELETE_CHARACTER,
  REMOVE_FAMILY_TREE_BACKGROUND_IMAGE,
  SET_FAMILY_TREE_BACKGROUND_COLOR,
  UNDO_N_TIMES,
  REDO_N_TIMES,
  UNDO,
  REDO,
  NEW_FILE,
  ACTION_CONFLICT_CHANGES,
  LOAD_FAMILY_TREES,
  LOAD_FAMILY_TREE,
  BATCH_LOAD_FAMILY_TREE,
  REMOVE_FAMILY_TREE,
} from '../constants/ActionTypes'
import { safeParseInt } from '../helpers/safeParseInt'
import { addConflictedFamilyTreeEntities } from './addConflictedEntities'

/**
 * @typedef {import('../types/plottr').FamilyTree} Family
 * @typedef {import('../types/plottr').Member} Member
 * @typedef {import('../index').PltrAction} PltrAction
 */

/**
 * @template A
 * @typedef {import('../types/reducer').DataRepairer<A>} DataRepairer
 */

const SIBLING = 'Sibling'
const PARENT = 'Parent'
const CHILD = 'Child'

const RELATIONSHIP_TO_COLOUR = {
  [capitalize(SIBLING)]: lineColors.nextColor(0),
  [capitalize(PARENT)]: lineColors.nextColor(1),
  [capitalize(CHILD)]: lineColors.nextColor(2),
}

/**
 * @typedef {Record<String, Family>} FamilyTreeState
 */

/**
 * @type {FamilyTreeState}
 */
const INITIAL_STATE = {
  0: {
    id: 0,
    name: '',
    stage: {
      scale: [1, 1],
      x: 0,
      y: 0,
    },
    members: {},
    backgroundImageId: null,
    backgroundColor: null,
    backgroundImageTranslation: null,
    relationshipConfiguration: {
      [SIBLING]: {
        name: capitalize(SIBLING),
        type: 'sibling',
        colour: RELATIONSHIP_TO_COLOUR['Sibling'],
        lineStyle: 'SOLID',
      },
      [PARENT]: {
        name: capitalize(PARENT),
        type: 'parent',
        colour: RELATIONSHIP_TO_COLOUR['Parent'],
        lineStyle: 'SOLID',
      },
      [CHILD]: {
        name: capitalize(CHILD),
        type: 'child',
        colour: RELATIONSHIP_TO_COLOUR['Child'],
        lineStyle: 'SOLID',
      },
    },
  },
}

/**
 * @param {DataRepairer<any>} _dataRepairers
 * @return {function(FamilyTreeState, PltrAction): FamilyTreeState}
 */
const familyTreeReducer =
  (_dataRepairers) =>
  (state = INITIAL_STATE, action) => {
    switch (action.type) {
      case SAVE_FAMILY_TREE: {
        const { familyTree } = action
        return {
          ...state,
          [familyTree.id]: familyTree,
        }
      }
      case SET_CURRENT_FAMILY_TREE_BACKGROUND: {
        return {
          ...state,
          [action.familyTreeId]: {
            ...state[action.familyTreeId],
            backgroundColor: null,
            backgroundImageId: action.backgroundImageId,
            backgroundImageTranslation: action.translation,
          },
        }
      }
      case SET_RELATIONSHIP_TYPE_COLOUR: {
        return {
          ...state,
          [action.familyTreeId]: {
            ...state[action.familyTreeId],
            relationshipConfiguration: {
              ...state[action.familyTreeId].relationshipConfiguration,
              [action.selectedRelationship]: {
                ...state[action.familyTreeId].relationshipConfiguration[
                  action.selectedRelationship
                ],
                colour: action.colour,
              },
            },
          },
        }
      }
      case SET_SELECTED_RELATIONSHIP_NAME: {
        if (state[action.familyTreeId].relationshipConfiguration[action.selectedRelationship]) {
          /**
           * @type {function(Record<String, Member>, [String, Member]): Record<String, Member>}
           */
          const accumulateMembers = (members, nextMember) => {
            const [memberId, member] = nextMember

            const containsRelationship = member.relationships.some(({ relationshipName }) => {
              return relationshipName === action.selectedRelationship
            })

            if (containsRelationship) {
              return {
                ...members,
                [memberId]: {
                  ...member,
                  relationships: member.relationships.map((relationship) => {
                    if (relationship.relationshipName === action.selectedRelationship) {
                      return {
                        ...relationship,
                        relationshipName: action.name,
                      }
                    } else {
                      return relationship
                    }
                  }),
                },
              }
            } else {
              return {
                ...members,
                [memberId]: member,
              }
            }
          }

          return {
            ...state,
            [action.familyTreeId]: {
              ...state[action.familyTreeId],
              members: Object.entries(state[action.familyTreeId].members).reduce(
                accumulateMembers,
                {}
              ),
              relationshipConfiguration: {
                ...omit(
                  state[action.familyTreeId].relationshipConfiguration,
                  action.selectedRelationship
                ),
                [action.name]: {
                  ...state[action.familyTreeId].relationshipConfiguration[
                    action.selectedRelationship
                  ],
                  name: action.name,
                },
              },
            },
          }
        } else {
          return state
        }
      }
      case SET_SELECTED_RELATIONSHIP_TYPE: {
        return {
          ...state,
          [action.familyTreeId]: {
            ...state[action.familyTreeId],
            relationshipConfiguration: {
              ...state[action.familyTreeId].relationshipConfiguration,
              [action.selectedRelationship]: {
                ...state[action.familyTreeId].relationshipConfiguration[
                  action.selectedRelationship
                ],
                type: action.relationshipType,
              },
            },
          },
        }
      }
      case SET_SELECTED_RELATIONSHIP_LINE_STYLE: {
        return {
          ...state,
          [action.familyTreeId]: {
            ...state[action.familyTreeId],
            relationshipConfiguration: {
              ...state[action.familyTreeId].relationshipConfiguration,
              [action.selectedRelationship]: {
                ...state[action.familyTreeId].relationshipConfiguration[
                  action.selectedRelationship
                ],
                lineStyle: action.lineStyle,
              },
            },
          },
        }
      }
      case DELETE_SELECTED_RELATIONSHIP: {
        const relationshipConfigToDelete =
          state[action.familyTreeId].relationshipConfiguration[action.selectedRelationship]
        if (relationshipConfigToDelete) {
          return {
            ...state,
            [action.familyTreeId]: {
              ...state[action.familyTreeId],
              relationshipConfiguration: omit(
                state[action.familyTreeId].relationshipConfiguration,
                action.selectedRelationship
              ),
              members: Object.entries(state[action.familyTreeId].members).reduce((acc, next) => {
                const [key, val] = next
                if (
                  val.relationships.some(({ relationshipName }) => {
                    return relationshipName === relationshipConfigToDelete.name
                  })
                ) {
                  return {
                    ...acc,
                    [key]: {
                      ...val,
                      relationships: val.relationships.filter((relationship) => {
                        return relationship.relationshipName !== relationshipConfigToDelete.name
                      }),
                    },
                  }
                } else {
                  return {
                    ...acc,
                    [key]: val,
                  }
                }
              }, {}),
            },
          }
        } else {
          return state
        }
      }
      case NEW_RELATIONSHIP_TYPE: {
        const currentRelationshipConfiguration =
          state[action.familyTreeId].relationshipConfiguration
        const relationshipName = action.relationshipName
        return {
          ...state,
          [action.familyTreeId]: {
            ...state[action.familyTreeId],
            relationshipConfiguration: {
              ...currentRelationshipConfiguration,
              [relationshipName]: {
                colour: 'black',
                name: relationshipName,
                type: 'child',
              },
            },
          },
        }
      }
      case NEW_FAMILY_TREE: {
        /**
         * @type {function(number | Null, string): number}
         */
        const accumulator = (max, next) => {
          if (typeof max === 'number') {
            const id = safeParseInt(next)
            if (id) {
              return Math.max(max, id)
            } else {
              return max
            }
          } else {
            return safeParseInt(next)
          }
        }
        const maxId = Object.keys(state).reduce(accumulator, null)
        const id = typeof maxId === 'number' ? maxId + 1 : 0
        return {
          ...state,
          [id]: {
            ...cloneDeep(emptyFamilyTree),
            id,
          },
        }
      }
      case DELETE_FAMILY_TREE: {
        if (typeof action.id === 'number' && state[action.id]) {
          return omit(state, action.id)
        } else {
          return state
        }
      }
      case EDIT_FAMILY_TREE_NAME: {
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            name: action.name,
          },
        }
      }
      case DELETE_CHARACTER: {
        return Object.entries(state).reduce((acc, next) => {
          const [treeId, tree] = next
          const withoutCharacter = omit(tree.members, action.id)
          const withoutRelationshipsreferencingCharacter = Object.entries(withoutCharacter).reduce(
            (memberAcc, nextMember) => {
              const [characterId, member] = nextMember
              return {
                ...memberAcc,
                [characterId]: {
                  ...member,
                  relationships: member.relationships
                    .filter((relationship) => {
                      return relationship.characterId !== action.id
                    })
                    .map((relationship) => {
                      return {
                        ...relationship,
                        ...(relationship.sharedSources
                          ? {
                              sharedSources: relationship.sharedSources.filter(
                                (sharedCharacterId) => {
                                  return sharedCharacterId !== action.id
                                }
                              ),
                            }
                          : {}),
                      }
                    }),
                },
              }
            },
            {}
          )
          return {
            ...acc,
            [treeId]: {
              ...tree,
              members: withoutRelationshipsreferencingCharacter,
            },
          }
        }, {})
      }
      case REMOVE_FAMILY_TREE_BACKGROUND_IMAGE: {
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            backgroundColor: null,
            backgroundImageId: null,
            backgroundImageTranslation: null,
          },
        }
      }
      case SET_FAMILY_TREE_BACKGROUND_COLOR: {
        if (state[action.familyTreeId]) {
          return {
            ...state,
            [action.familyTreeId]: {
              ...state[action.familyTreeId],
              backgroundColor: action.color,
              backgroundImageId: null,
              backgroundImageTranslation: null,
            },
          }
        } else {
          return state
        }
      }
      case ACTION_CONFLICT_CHANGES: {
        return addConflictedFamilyTreeEntities(
          action.onlineFile.familyTrees,
          action.localOnly,
          action.changedLocalAndOnline,
          action.idMapping
        )
      }
      case FILE_LOADED: {
        return action.data.familyTrees ?? INITIAL_STATE
      }
      case NEW_FILE: {
        return newFileFamilyTrees
      }
      case LOAD_FAMILY_TREES: {
        return action.familyTrees
      }
      case LOAD_FAMILY_TREE: {
        return {
          ...state,
          [action.familyTree.id]: action.familyTree,
        }
      }
      case BATCH_LOAD_FAMILY_TREE: {
        return action.familyTrees
      }
      case REMOVE_FAMILY_TREE: {
        return omit(state, action.familyTree.id)
      }
      case UNDO_N_TIMES:
      case REDO_N_TIMES:
      case UNDO:
      case REDO: {
        if (action.state.familyTrees && typeof action.state.familyTrees === 'object') {
          return action.state.familyTrees
        } else {
          return state
        }
      }
      default: {
        return state
      }
    }
  }

export default familyTreeReducer
